import React, { useState, useEffect } from 'react';
import { Plus, BarChart3, Filter, Search, Calendar, Bell, FileText, LogOut, X } from 'lucide-react';
import Dashboard from './components/Dashboard';
import FormActividad from './components/FormActividad';
import ListaActividades from './components/ListaActividades';
import CalendarView from './components/CalendarView';
import Login from './components/Login';
import { servicioActividades, servicioAuth } from './services/firebaseService';
import { generarReportePDF, exportarExcel } from './utils/reportUtils';
import { mostrarNotificacion } from './utils/notificationUtils';
import logoLiceo from './assets/logo-liceo.png';

const ActivityManager = ({ usuarioActual }) => {
  const [actividades, setActividades] = useState([]);
  const [mostrarFormulario, setMostrarFormulario] = useState(false);
  const [mostrarDashboard, setMostrarDashboard] = useState(true);
  const [filtroMes, setFiltroMes] = useState(0);
  const [filtroEstado, setFiltroEstado] = useState('todos');
  const [filtroEtiqueta, setFiltroEtiqueta] = useState('todas');
  const [busqueda, setBusqueda] = useState('');
  const [actividadEditar, setActividadEditar] = useState(null);
  const [vista, setVista] = useState('lista');
  const [notificacionesActivas, setNotificacionesActivas] = useState(true);
  const [cargando, setCargando] = useState(true);

  const meses = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 
                 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

  const estados = ['sin iniciar', 'en proceso', 'finalizada', 'cancelada', 'programada'];
  const etiquetasDisponibles = ['Académica', 'Administrativa', 'Extracurricular', 'Bienestar', 'Otra'];

  useEffect(() => {
    if (!usuarioActual) {
      console.log('No hay usuario actual');
      return;
    }

    console.log('Iniciando escucha de actividades');
    setCargando(true);
    
    const unsubscribe = servicioActividades.escucharActividades(
      usuarioActual.uid,
      (actividadesFirebase) => {
        console.log('Actividades recibidas:', actividadesFirebase.length);
        setActividades(actividadesFirebase);
        setCargando(false);
      }
    );

    return () => {
      console.log('Deteniendo escucha');
      unsubscribe();
    };
  }, [usuarioActual]);

  useEffect(() => {
    if (!notificacionesActivas || actividades.length === 0) return;
    
    const verificarNotificaciones = () => {
      const hoy = new Date();
      hoy.setHours(0, 0, 0, 0);

      actividades.forEach(actividad => {
        const fechaInicio = new Date(actividad.fechaInicio);
        fechaInicio.setHours(0, 0, 0, 0);
        
        const diasFaltantes = Math.floor((fechaInicio - hoy) / (1000 * 60 * 60 * 24));
        
        if (diasFaltantes === 1 && (actividad.estado === 'programada' || actividad.estado === 'sin iniciar')) {
          mostrarNotificacion(`⏰ ${actividad.nombre} es mañana`, 'info');
        }
        
        if (diasFaltantes === 0 && actividad.estado === 'programada') {
          mostrarNotificacion(`🚀 ${actividad.nombre} inicia HOY`, 'info');
        }
      });
    };
    
    verificarNotificaciones();
    const intervalo = setInterval(verificarNotificaciones, 3600000);
    
    return () => clearInterval(intervalo);
  }, [actividades, notificacionesActivas]);

  const agregarActividad = async (nuevaActividad, frecuencia = 'ninguna') => {
    try {
      if (frecuencia === 'ninguna' || !frecuencia) {
        const actividadGuardar = {
          ...nuevaActividad,
          estado: nuevaActividad.estado || 'programada'
        };

        if (actividadEditar) {
          await servicioActividades.actualizar(actividadEditar.id, actividadGuardar);
          mostrarNotificacion('Actividad actualizada', 'éxito');
          setActividadEditar(null);
        } else {
          await servicioActividades.crear(actividadGuardar, usuarioActual.uid);
          mostrarNotificacion('Actividad creada', 'éxito');
        }
      } else {
        const fechaInicio = new Date(nuevaActividad.fechaInicio);
        const fechaCierre = new Date(nuevaActividad.fechaCierre);

        let numRepeticiones = 1;
        let incrementoDias = 0;

        if (frecuencia === 'diaria') {
          numRepeticiones = 30;
          incrementoDias = 1;
        } else if (frecuencia === 'semanal') {
          numRepeticiones = 12;
          incrementoDias = 7;
        } else if (frecuencia === 'quincenal') {
          numRepeticiones = 6;
          incrementoDias = 14;
        } else if (frecuencia === 'mensual') {
          numRepeticiones = 12;
          incrementoDias = 30;
        }

        for (let i = 0; i < numRepeticiones; i++) {
          const novaFechaInicio = new Date(fechaInicio);
          novaFechaInicio.setDate(novaFechaInicio.getDate() + (incrementoDias * i));

          const novaFechaCierre = new Date(fechaCierre);
          novaFechaCierre.setDate(novaFechaCierre.getDate() + (incrementoDias * i));

          const formatoFechaInicio = novaFechaInicio.toISOString().split('T')[0];
          const formatoFechaCierre = novaFechaCierre.toISOString().split('T')[0];

          await servicioActividades.crear(
            {
              ...nuevaActividad,
              fechaInicio: formatoFechaInicio,
              fechaCierre: formatoFechaCierre,
              estado: nuevaActividad.estado || 'programada',
              recurrencia: frecuencia,
            },
            usuarioActual.uid
          );
        }

        mostrarNotificacion(`${numRepeticiones} actividades creadas`, 'éxito');
      }

      setMostrarFormulario(false);
    } catch (error) {
      mostrarNotificacion('Error al guardar: ' + error.message, 'error');
    }
  };

  const eliminarActividad = (id) => {
    if (window.confirm('¿Estás seguro?')) {
      servicioActividades.eliminar(id)
        .then(() => mostrarNotificacion('Actividad eliminada', 'éxito'))
        .catch(error => mostrarNotificacion('Error: ' + error.message, 'error'));
    }
  };

  const editarActividad = (actividad) => {
    setActividadEditar(actividad);
    setMostrarFormulario(true);
  };

  const actualizarEstadoActividad = async (id, nuevoEstado) => {
    try {
      await servicioActividades.actualizar(id, { estado: nuevoEstado });
      mostrarNotificacion('Estado actualizado', 'éxito');
    } catch (error) {
      mostrarNotificacion('Error: ' + error.message, 'error');
    }
  };

  const agregarSubtarea = async (actividadId, subtarea) => {
    try {
      await servicioActividades.agregarSubtarea(actividadId, subtarea);
      mostrarNotificacion('Subtarea agregada', 'éxito');
    } catch (error) {
      mostrarNotificacion('Error: ' + error.message, 'error');
    }
  };

  const completarSubtarea = async (actividadId, subtareaId) => {
    try {
      const actividad = actividades.find(a => a.id === actividadId);
      if (!actividad || !actividad.subtareas) return;
      
      const subtareas = actividad.subtareas.map(s =>
        s.id === subtareaId ? { ...s, completada: !s.completada } : s
      );
      await servicioActividades.actualizar(actividadId, { subtareas });
    } catch (error) {
      mostrarNotificacion('Error: ' + error.message, 'error');
    }
  };

  const agregarComentario = async (actividadId, comentario) => {
    try {
      await servicioActividades.agregarComentario(actividadId, comentario, usuarioActual.email);
      mostrarNotificacion('Comentario agregado', 'éxito');
    } catch (error) {
      mostrarNotificacion('Error: ' + error.message, 'error');
    }
  };

  const actividadesFiltradas = actividades.filter(a => {
    if (!a) return false;

    let mesCoincide = true;
    if (filtroMes !== 0) {
      try {
        const mesActividad = parseInt(a.fechaInicio?.split('-')[1] || '0');
        mesCoincide = mesActividad === filtroMes;
      } catch (e) {
        mesCoincide = true;
      }
    }
    
    const estadoCoincide = filtroEstado === 'todos' || (a.estado && a.estado === filtroEstado);
    
    const etiquetaCoincide = filtroEtiqueta === 'todas' || 
                             (a.etiquetas && Array.isArray(a.etiquetas) && a.etiquetas.includes(filtroEtiqueta));
    
    let busquedaCoincide = true;
    if (busqueda && busqueda.trim() !== '') {
      const termino = busqueda.toLowerCase().trim();
      busquedaCoincide = 
        (a.nombre && a.nombre.toLowerCase().includes(termino)) ||
        (a.proposito && a.proposito.toLowerCase().includes(termino)) ||
        (a.responsable && a.responsable.toLowerCase().includes(termino));
    }

    return mesCoincide && estadoCoincide && etiquetaCoincide && busquedaCoincide;
  });

  const generarReporte = () => {
    generarReportePDF(actividadesFiltradas, meses, actividades);
    mostrarNotificacion('Reporte abierto - Presiona Ctrl+P para guardar', 'info');
  };

  const handleExportarExcel = () => {
    exportarExcel(actividadesFiltradas);
    mostrarNotificacion('Excel descargado', 'éxito');
  };

  const handleLogout = async () => {
    await servicioAuth.logout();
  };

  if (cargando) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando actividades...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <div id="notification-container" className="fixed top-4 right-4 z-50 space-y-2" />

      <header className="bg-white shadow-md sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 mb-4">
            <div className="flex items-center gap-4">
              <img 
                src={logoLiceo} 
                alt="Logo Liceo Ángel Emilio Casado" 
                className="w-16 h-16 object-contain"
              />
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">Liceo Ángel Emilio Casado</h1>
                <p className="text-gray-600 text-sm">Usuario: {usuarioActual?.email}</p>
              </div>
            </div>

            <div className="flex gap-2 flex-wrap">
              <button onClick={() => setMostrarDashboard(!mostrarDashboard)} className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition text-sm">
                <BarChart3 size={18} /> Dashboard
              </button>
              <button onClick={() => setVista(vista === 'lista' ? 'calendario' : 'lista')} className="flex items-center gap-2 px-3 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition text-sm">
                <Calendar size={18} /> {vista === 'lista' ? 'Calendario' : 'Lista'}
              </button>
              <button onClick={() => { setMostrarFormulario(!mostrarFormulario); setActividadEditar(null); }} className="flex items-center gap-2 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition text-sm">
                <Plus size={18} /> Agregar
              </button>
              <button onClick={generarReporte} className="flex items-center gap-2 px-3 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition text-sm">
                <FileText size={18} /> PDF
              </button>
              <button onClick={handleExportarExcel} className="flex items-center gap-2 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition text-sm">
                <FileText size={18} /> Excel
              </button>
              <button onClick={() => setNotificacionesActivas(!notificacionesActivas)} className={`flex items-center gap-2 px-3 py-2 rounded-lg transition text-sm ${notificacionesActivas ? 'bg-yellow-600 hover:bg-yellow-700' : 'bg-gray-400 hover:bg-gray-500'} text-white`}>
                <Bell size={18} /> {notificacionesActivas ? 'ON' : 'OFF'}
              </button>
              <button onClick={handleLogout} className="flex items-center gap-2 px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition text-sm">
                <LogOut size={18} /> Salir
              </button>
            </div>
          </div>

          <div className="relative">
            <Search className="absolute left-3 top-3 text-gray-400" size={20} />
            <input 
              type="text" 
              placeholder="🔍 Buscar actividades por nombre, propósito o responsable..." 
              value={busqueda} 
              onChange={(e) => setBusqueda(e.target.value)} 
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {mostrarFormulario && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
            <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full my-8">
              <div className="flex justify-between items-center p-6 border-b border-gray-200 sticky top-0 bg-white">
                <h2 className="text-2xl font-bold text-gray-800">{actividadEditar ? '✏️ Editar' : '➕ Nueva Actividad'}</h2>
                <button onClick={() => { setMostrarFormulario(false); setActividadEditar(null); }} className="text-gray-500 hover:text-gray-700">
                  <X size={24} />
                </button>
              </div>
              <div className="p-6 overflow-y-auto max-h-[calc(100vh-200px)]">
                <FormActividad onGuardar={agregarActividad} onCancelar={() => { setMostrarFormulario(false); setActividadEditar(null); }} actividadEditar={actividadEditar} meses={meses} etiquetasDisponibles={etiquetasDisponibles} esRecurrente={true} />
              </div>
            </div>
          </div>
        )}

        {mostrarDashboard && <Dashboard actividades={actividades} meses={meses} />}

        <div className="bg-white rounded-lg shadow-md p-4 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Filter size={20} className="text-gray-600" />
            <span className="font-semibold">Filtros:</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <select value={filtroMes} onChange={(e) => setFiltroMes(parseInt(e.target.value))} className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option value={0}>📅 Todos</option>
              {meses.map((mes, index) => (
                <option key={index} value={index + 1}>{mes}</option>
              ))}
            </select>
            <select value={filtroEstado} onChange={(e) => setFiltroEstado(e.target.value)} className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option value="todos">📊 Todos</option>
              {estados.map((estado) => (
                <option key={estado} value={estado}>{estado}</option>
              ))}
            </select>
            <select value={filtroEtiqueta} onChange={(e) => setFiltroEtiqueta(e.target.value)} className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option value="todas">🏷️ Todas</option>
              {etiquetasDisponibles.map((etiqueta) => (
                <option key={etiqueta} value={etiqueta}>{etiqueta}</option>
              ))}
            </select>
            <button onClick={() => { setFiltroMes(0); setFiltroEstado('todos'); setFiltroEtiqueta('todas'); setBusqueda(''); }} className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition">
              🔄 Limpiar
            </button>
          </div>
          <p className="text-sm text-gray-600 mt-3">
            {actividadesFiltradas.length} de {actividades.length} actividades
          </p>
        </div>

        {vista === 'calendario' ? (
          <CalendarView actividades={actividadesFiltradas} meses={meses} />
        ) : (
          <ListaActividades actividades={actividadesFiltradas} onEditar={editarActividad} onEliminar={eliminarActividad} onCambiarEstado={actualizarEstadoActividad} onAgregarSubtarea={agregarSubtarea} onCompletarSubtarea={completarSubtarea} onAgregarComentario={agregarComentario} meses={meses} />
        )}

        {actividadesFiltradas.length === 0 && !mostrarFormulario && (
          <div className="text-center py-12 bg-white rounded-lg shadow-md">
            <p className="text-gray-500 text-lg">📭 {actividades.length === 0 ? 'No hay actividades aún' : 'No hay actividades con esos filtros'}</p>
          </div>
        )}
      </main>
    </div>
  );
};

export default ActivityManager;
